/**
 * Admin › Platform: the machinery. Four tabs, each a panel that used to be
 * its own index entry:
 *   Runtime   — active turns, incomplete checkpoints, recent turns and tools
 *   Readiness — pre-flight check and the production-safe smoke run
 *   Releases  — the desktop release catalog and rollout levers
 *   Services  — connected-service counts across workspaces
 */

import { useCallback, useEffect, useState } from "react";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  RefreshCw,
  Server,
  Trash2,
  XCircle,
  Zap,
} from "lucide-react";
import { Badge } from "../../ui/badge.js";
import { Button } from "../../ui/button.js";
import { RunReadinessPanel } from "../../components/admin/RunReadinessPanel.js";
import { DesktopReleasesPanel } from "../../components/admin/DesktopReleasesPanel.js";
import {
  Checkpoint,
  ConnectedServiceRow,
  LoadingSpinner,
  RecentTurn,
  SectionHeader,
  StatCard,
  SubTabs,
  Table,
  Td,
  Th,
  ToolExecution,
  formatCost,
  formatTokens,
  timeAgo,
} from "./shared.js";

export type PlatformTab = "runtime" | "readiness" | "releases" | "services";

export const PLATFORM_TABS: Array<{ key: PlatformTab; label: string; to: string }> = [
  { key: "runtime", label: "Runtime", to: "/admin/platform/runtime" },
  { key: "readiness", label: "Run readiness", to: "/admin/platform/readiness" },
  { key: "releases", label: "Desktop releases", to: "/admin/platform/releases" },
  { key: "services", label: "Connected services", to: "/admin/platform/services" },
];

export function PlatformPage({ tab }: { tab: PlatformTab }) {
  return (
    <div className="space-y-5">
      <SubTabs tabs={PLATFORM_TABS} active={tab} label="Platform sections" />
      {tab === "runtime" && <RuntimePanel />}
      {tab === "readiness" && <RunReadinessPanel />}
      {tab === "releases" && <DesktopReleasesPanel />}
      {tab === "services" && <ServicesPanel />}
    </div>
  );
}

/* ─── Runtime ───────────────────────────────────────────────── */

function RuntimePanel() {
  const [turns, setTurns] = useState<RecentTurn[]>([]);
  const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([]);
  const [toolExecs, setToolExecs] = useState<ToolExecution[]>([]);
  const [activeInfo, setActiveInfo] = useState<{ count: number; draining: boolean }>({ count: 0, draining: false });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch("/api/admin/runtime/active-turns").then((r) => r.json()),
      fetch("/api/admin/runtime/recent-turns?limit=25").then((r) => r.json()),
      fetch("/api/admin/runtime/checkpoints").then((r) => r.json()),
      fetch("/api/admin/runtime/tool-executions?limit=25").then((r) => r.json()),
    ])
      .then(([active, t, cp, te]) => {
        setActiveInfo(active);
        setTurns(t);
        setCheckpoints(cp);
        setToolExecs(te);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function deleteCheckpoint(turnId: string) {
    await fetch(`/api/admin/runtime/checkpoints/${turnId}`, { method: "DELETE" });
    setCheckpoints((prev) => prev.filter((c) => c.turn_id !== turnId));
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Active Turns" value={activeInfo.count} icon={<Zap size={16} style={{ color: "var(--warning)" }} />} />
        <StatCard
          label="Draining"
          value={activeInfo.draining ? "Yes" : "No"}
          icon={<AlertTriangle size={16} style={{ color: activeInfo.draining ? "var(--error)" : "var(--success)" }} />}
        />
        <StatCard label="Incomplete Checkpoints" value={checkpoints.length} icon={<Clock size={16} style={{ color: "var(--info)" }} />} />
      </div>

      <div className="flex items-center gap-2 mb-3">
        <Button
          type="button"
          size="sm"
          onClick={refresh}
          className="bg-[var(--accent-muted)] text-[var(--accent)] hover:bg-[var(--accent-muted)]/80"
        >
          <RefreshCw size={12} /> Refresh
        </Button>
      </div>

      {checkpoints.length > 0 && (
        <div>
          <SectionHeader title="Incomplete Checkpoints" icon={<AlertTriangle size={16} style={{ color: "var(--warning)" }} />} />
          <Table>
            <thead>
              <tr>
                <Th>Turn ID</Th>
                <Th>Agent</Th>
                <Th>Node</Th>
                <Th>Updated</Th>
                <Th>Action</Th>
              </tr>
            </thead>
            <tbody>
              {checkpoints.map((cp) => (
                <tr key={cp.turn_id}>
                  <Td mono>{cp.turn_id.slice(0, 8)}...</Td>
                  <Td mono>{cp.agent_id}</Td>
                  <Td>
                    <Badge size="xs" className="bg-[var(--accent-muted)] text-[var(--accent)]">
                      {cp.node}
                    </Badge>
                  </Td>
                  <Td>{timeAgo(cp.updated_at)}</Td>
                  <Td>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => void deleteCheckpoint(cp.turn_id)}
                      className="text-[var(--error)] hover:bg-[var(--error-muted)]"
                    >
                      <Trash2 size={10} /> Delete
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <div>
        <SectionHeader title="Recent Turns" icon={<Zap size={16} style={{ color: "var(--warning)" }} />} />
        <Table>
          <thead>
            <tr>
              <Th>Agent</Th>
              <Th>Workspace</Th>
              <Th>Model</Th>
              <Th>Tokens</Th>
              <Th>Cost</Th>
              <Th>Duration</Th>
              <Th>When</Th>
            </tr>
          </thead>
          <tbody>
            {turns.map((t) => (
              <tr key={t.id}>
                <Td mono>{t.agent_id}</Td>
                <Td>{t.workspace_name ?? "—"}</Td>
                <Td mono>{t.model}</Td>
                <Td>{formatTokens(t.prompt_tokens + t.completion_tokens)}</Td>
                <Td>{formatCost(t.cost_millicents)}</Td>
                <Td>{(t.duration_ms / 1000).toFixed(1)}s</Td>
                <Td>{timeAgo(t.created_at)}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div>
        <SectionHeader title="Recent Tool Executions" icon={<Activity size={16} style={{ color: "var(--success)" }} />} />
        <Table>
          <thead>
            <tr>
              <Th>Tool</Th>
              <Th>Status</Th>
              <Th>Started</Th>
              <Th>Completed</Th>
            </tr>
          </thead>
          <tbody>
            {toolExecs.map((te) => (
              <tr key={te.tool_call_id}>
                <Td mono>{te.tool_name}</Td>
                <Td>
                  <span className="inline-flex items-center gap-1">
                    {te.status === "completed" ? (
                      <CheckCircle2 size={12} style={{ color: "var(--success)" }} />
                    ) : te.status === "failed" ? (
                      <XCircle size={12} style={{ color: "var(--error)" }} />
                    ) : (
                      <Loader2 size={12} className="animate-spin" style={{ color: "var(--warning)" }} />
                    )}
                    {te.status}
                  </span>
                </Td>
                <Td>{timeAgo(te.started_at)}</Td>
                <Td>{te.completed_at ? timeAgo(te.completed_at) : "—"}</Td>
              </tr>
            ))}
            {toolExecs.length === 0 && (
              <tr>
                <Td>No recent tool executions</Td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

/* ─── Connected services ────────────────────────────────────── */

function ServicesPanel() {
  const [services, setServices] = useState<ConnectedServiceRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/admin/system/connected-services")
      .then((r) => r.json())
      .then((s) => {
        setServices(s);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <SectionHeader title="Connected Services" icon={<Server size={16} style={{ color: "var(--info)" }} />} />
      <Table>
        <thead>
          <tr>
            <Th>Service Type</Th>
            <Th>Connections</Th>
            <Th>Workspaces</Th>
          </tr>
        </thead>
        <tbody>
          {services.map((s) => (
            <tr key={s.service_type}>
              <Td mono>{s.service_type}</Td>
              <Td>{s.count}</Td>
              <Td>{s.workspace_count}</Td>
            </tr>
          ))}
          {services.length === 0 && (
            <tr>
              <Td>No connected services</Td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
}
